import get from "lodash/get";
import { useEffect, useRef, useState } from "react";
import submit from "submit-form";
import { AU_STATES, STRIPE, US_STATES } from "../constants";
import BackArrowButton from "../parts/back-arrow-button";
import Select from "../parts/inputs/select";
import LoginWrapper from "./LoginWrapper";

function elementsStyle() {
  let fontColor = "";
  let placeholderColor = "";
  const htmlTags = document.getElementsByTagName("html");

  Array.prototype.slice.call(htmlTags).forEach((tag) => {
    if (tag?.attributes?.["data-theme"]?.value === "light") {
      fontColor = "rgba(0, 0, 0, 0.85)";
      placeholderColor = "rgba(0, 0, 0, 0.3)";
    } else {
      fontColor = "rgba(255, 255, 255, 0.85)";
      placeholderColor = "rgba(255, 255, 255, 0.3)";
    }
  });

  return {
    base: {
      color: fontColor,
      lineHeight: "19px",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: globalThis.matchMedia("(min-width: 2100px)").matches
        ? "22px"
        : window.matchMedia("(min-width: 1700px)").matches
          ? "18px"
          : "14px",
      fontWeight: 600,
      iconColor: "rgba(255, 255, 255, 0.7)",
      "::placeholder": {
        color: placeholderColor,
      },
    },
    invalid: {
      color: "rgba(255, 56, 82, 1)",
      iconColor: "rgba(255, 56, 82, 1)",
    },
  };
}

function View({ activeUser, stripeKey, countries, getCountryName }) {
  const [billingInfo, setBillingInfo] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: get(activeUser, "country", ""),
  });
  const billingInfoRef = useRef(billingInfo);

  useEffect(() => {
    billingInfoRef.current = billingInfo;
  }, [billingInfo]);

  useEffect(() => {
    const stripe = globalThis.Stripe(stripeKey);
    const elements = stripe.elements();

    const card = elements.create("card", { style: elementsStyle() });
    card.mount("#card-element");

    card.addEventListener("change", (event) => {
      const displayError = document.getElementById("card-errors");
      displayError.textContent = event.error ? event.error.message : "";
    });

    const form = document.getElementById("payment-form");

    form.addEventListener("submit", (event) => {
      event.preventDefault();

      const { addressLine1, addressLine2, city, state, country } =
        billingInfoRef.current;

      const tokenData = {
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_city: city,
        address_state: state,
        address_country: country,
      };

      stripe.createToken(card, tokenData).then((result) => {
        if (result.error) {
          const errorElement = document.getElementById("card-errors");
          errorElement.textContent = result.error.message;
        } else {
          const stripeToken = result.token;
          const hiddenInput = document.createElement("input");
          hiddenInput.setAttribute("type", "hidden");
          hiddenInput.setAttribute("name", "stripeToken");
          hiddenInput.setAttribute("value", stripeToken.id);
          form.appendChild(hiddenInput);

          submit("/actions/billing/perform", {
            method: "POST",
            body: {
              source: tokenData,
              stripeToken: stripeToken.id,
            },
          });
        }
      });
    });
  }, [stripeKey]);

  function updateBillingInfo(updates) {
    setBillingInfo((prev) => ({ ...prev, ...updates }));
  }

  const handleReturnButton = () => {
    submit("logout", { method: "GET" });
  };

  const { addressLine1, addressLine2, city, country, state } = billingInfo;

  const countryOpts = countries.map((c) => ({
    value: c,
    label: getCountryName(c),
  }));
  const usStates = US_STATES.map((s) => ({
    value: s.abbreviation,
    label: s.name,
  }));
  const auStates = AU_STATES.map((s) => ({
    value: s.abbreviation,
    label: s.name,
  }));

  return (
    <LoginWrapper
      mainClass="Billing"
      left={
        <form
          action="/actions/billing/perform"
          method="post"
          id="payment-form"
          className="billing"
        >
          <BackArrowButton
            onBack={(_) => handleReturnButton()}
            classNames="mobile-hidden"
          />

          <section className="mobile-hidden">
            <h2 className="clipped text-center">
              Your PERFORM subscription needs attention
            </h2>
            <p className="text-center mt-3">
              Your recent payment didn't go through. Please update your payment
              details to reactivate your PERFORM subscription and continue
              managing your gym members.
            </p>
          </section>

          <header className="only-show-mobile">
            <section>
              <BackArrowButton onBack={(_) => handleReturnButton()} />

              <h2 className="clipped text-center">
                Your PERFORM subscription needs attention
              </h2>
            </section>
            <p className="text-center mt-3">
              Your recent payment didn't go through. Please update your payment
              details to reactivate your PERFORM subscription and continue
              managing your gym members.
            </p>
          </header>

          <section>
            <label className="only-show-mobile" htmlFor="cardNumber">
              Card Number
            </label>
            <div id="card-element" />
            <p id="card-errors" role="alert" />

            <article>
              <label htmlFor="source[address_line1]">Billing Address</label>
              <div className="mt-0!">
                <input
                  name="source[address_line1]"
                  type="text"
                  placeholder="Address Line 1"
                  required
                  value={addressLine1}
                  onChange={(e) =>
                    updateBillingInfo({ addressLine1: e.target.value })
                  }
                  sz="large"
                />

                <input
                  name="source[address_line2]"
                  type="text"
                  placeholder="Address Line 2"
                  value={addressLine2}
                  onChange={(e) =>
                    updateBillingInfo({ addressLine2: e.target.value })
                  }
                  sz="large"
                />
              </div>

              <div>
                <div>
                  <label htmlFor="source[address_city]">City</label>
                  <input
                    name="source[address_city]"
                    type="text"
                    placeholder="City"
                    value={city}
                    required
                    onChange={(e) =>
                      updateBillingInfo({ city: e.target.value })
                    }
                    sz="large"
                  />
                </div>

                <div>
                  <label htmlFor="source[address_country]">Country</label>
                  <Select
                    name="source[address_country]"
                    value={country}
                    options={countryOpts}
                    onChange={(country) => updateBillingInfo({ country })}
                  />
                </div>

                {country === "CA" && (
                  <div>
                    <label htmlFor="source[address_state]">Province</label>
                    <Select
                      name="source[address_state]"
                      value={state}
                      onChange={(state) => updateBillingInfo({ state })}
                      options={[
                        { value: "", label: "Province" },
                        { value: "AB", label: "Alberta" },
                        { value: "BC", label: "British Columbia" },
                        { value: "MB", label: "Manitoba" },
                        { value: "NB", label: "New Brunswick" },
                        { value: "NL", label: "Newfoundland and Labrador" },
                        { value: "NS", label: "Nova Scotia" },
                        { value: "ON", label: "Ontario" },
                        { value: "PE", label: "Prince Edward Island" },
                        { value: "QC", label: "Quebec" },
                        { value: "SK", label: "Saskatchewan" },
                        { value: "NT", label: "Northwest Territories" },
                        { value: "NU", label: "Nunavut" },
                        { value: "YT", label: "Yukon" },
                      ]}
                    />
                  </div>
                )}

                {country === "US" && (
                  <div>
                    <label htmlFor="source[address_state]">State</label>
                    <Select
                      name="source[address_state]"
                      value={state}
                      onChange={(state) => updateBillingInfo({ state })}
                      options={usStates}
                    />
                  </div>
                )}

                {country === "AU" && (
                  <div>
                    <label htmlFor="source[address_state]">State</label>
                    <Select
                      name="source[address_state]"
                      value={state}
                      onChange={(state) => updateBillingInfo({ state })}
                      options={auStates}
                    />
                  </div>
                )}

                {!["AU", "CA", "US"].includes(country) && (
                  <div>
                    <label htmlFor="source[address_state]">State</label>
                    <input
                      name="source[address_state]"
                      type="text"
                      placeholder="State"
                      value={state}
                      onChange={(e) =>
                        updateBillingInfo({ state: e.target.value })
                      }
                      sz="large"
                    />
                  </div>
                )}
              </div>
            </article>
          </section>

          <footer>
            <button
              data-button-type="primary"
              sz="large"
              className="px-10"
              type="submit"
            >
              Reactivate Subscription
            </button>

            <div>
              <span>Need help? </span>
              <a
                data-button-type="outline"
                sz="large"
                href="/coaching-portal/support/contact"
                role="button"
              >
                Contact Support
              </a>
            </div>
          </footer>
        </form>
      }
    />
  );
}

const PerformBilling = (app) => {
  app.get("/perform-billing", async ({ res, locals }) => {
    const { activeUser } = locals;
    if (!activeUser) {
      res.redirect("/login");
      return;
    }

    const stripeKey = STRIPE.PUBLISHABLE_KEY;

    res.body = (
      <View
        stripeKey={stripeKey}
        activeUser={activeUser}
        countries={locals.countries}
        getCountryName={locals.getCountryName}
      />
    );
  });
};

export default PerformBilling;
